import type {
  AgentConfig,
  AuthenticateConfig,
  CloseCallback,
  DirectoryProps,
  IntentResolutionMessage,
  RequestStorageAccessConfig,
  ResolveCallback,
  ResolveCallbackProps,
} from "@connectifi/agent-web";
import { Channel, ContextHandler, DesktopAgent } from "@finos/fdc3";
import React, {
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import {
  AgentAuthenticator,
  AgentEvent,
  AgentResolver,
  AgentState,
  AgentStorageAccess,
  ConnectifiAgent,
  UseConnectifiAgentProps,
} from "./types";
import { AgentImportContext } from "./agent-import-context";

const AgentStateReducer: Reducer<AgentState, AgentEvent> = (
  state: AgentState,
  event: AgentEvent
) => {
  switch (state) {
    case 'initial':
      if (event === 'connect') return 'connected';
      if (event === 'auth-challenge') return 'wait-for-auth';
      if (event === 'error') return 'error';
      break;
    case 'wait-for-auth':
      if (event === 'connect') return 'connected';
      if (event === 'error') return 'error';
      break;
    case "connected":
      if (event === "disconnect") return "reconnecting";
      break;
    case 'reconnecting':
      if (event === 'connect') return 'connected';
      if (event === 'disconnect-fatal') return 'disconnected';
      break;
  }
  return state;
};

export function useConnectifiAgent({
  appId,
  customAuthenticator,
}: Readonly<UseConnectifiAgentProps>): ConnectifiAgent {
  const iframeEmbedErrorPrefix = "failed to embed iframe: ";

  const agentImport = useContext(AgentImportContext);
  const [state, dispatch] = useReducer(AgentStateReducer, "initial");

  const [fdc3, setFDC3] = useState<DesktopAgent | undefined>(undefined);
  const [resolver, setResolver] = useState<AgentResolver | undefined>(
    undefined
  );
  const [authenticator, setAuthenticator] = useState<
    AgentAuthenticator | undefined
  >(undefined);
  const [directoryProps, setDirectoryProps] = useState<
    DirectoryProps | undefined
  >(undefined);
  const [sessionOwner, setSessionOwner] = useState<string | undefined>(
    undefined
  );
  const [anchorElement, setAnchorElement] = useState<Element | undefined>(
    undefined
  );
  const [localHandlers, setLocalHandlers] = useState<
    Map<string, ContextHandler>
  >(new Map());
  const [channels, setChannels] = useState<Channel[]>([]);
  const [currentChannelId, setCurrentChannelId] = useState<string | undefined>(
    undefined
  );
  const [storageAccess, setStorageAccess] = useState<
    AgentStorageAccess | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [hasBeenConnected, setHasBeenConnected] = useState<boolean>(false);
  const [reconnectingIn, setReconnectingIn] = useState<number>(-1);

  const resetResolver = () => {
    setResolver(undefined);
  };

  const resetAuthenticator = () => {
    setAuthenticator(undefined);
  };

  const resetStorageAccess = () => {
    setStorageAccess(undefined);
  };

  const handleIntentResolution = (
    message: IntentResolutionMessage,
    callback: ResolveCallback,
    closeCallback: CloseCallback
  ) => {
    setResolver({
      resolutionType: message.resolutionType,
      context: message.context,
      data: message.data,
      resolve: (props: ResolveCallbackProps) => {
        callback(props);
        resetResolver();
      },
      cancel: () => {
        closeCallback();
        resetResolver();
      },
    });
  };

  const handleAuthenticate = async (authConf: AuthenticateConfig) => {
    const { authenticate, ...dirProps } = authConf;
    setDirectoryProps(dirProps);
    dispatch('auth-challenge');
    return new Promise<void>((resolve, reject) => {
      setAuthenticator({
        resolve: () => {
          resetAuthenticator();
          if (customAuthenticator) {
            customAuthenticator(authConf).then(resolve);
          } else {
            authenticate().then(resolve);
          }
        },
        cancel: () => {
          resetAuthenticator();
          reject(new Error('auth canceled'));
          dispatch('error');
        },
      });
    });
  };

  const handleRequestStorageAccess = async ({
    button,
    waitForStorageAccess,
    requestConsent,
  }: RequestStorageAccessConfig) => {
    const buttonNode = (
      <div dangerouslySetInnerHTML={{ __html: button.outerHTML }}></div>
    );
    setStorageAccess({ button: buttonNode });

    const success = await waitForStorageAccess();
    if (!success) {
      await requestConsent("sameWindow");
    }

    resetStorageAccess();
  };

  const createTheAgent = useCallback(async () => {
    const agentConfig: AgentConfig = {
      bridgeGlobal: true,
      logLevel: "debug",
      headless: true,
      handleIntentResolution,
      handleAuthenticate,
      handleRequestStorageAccess,
      onSessionStarted: (props, user) => {
        setDirectoryProps(props);
        setSessionOwner(user);
      },
      onSessionError: (errorMsg: string) => {
        dispatch("error");
        setErrorMessage(errorMsg);
      },
      onConnected: () => {
        dispatch("connect");
        setReconnectingIn(0);
      },
      onDisconnected: (reconnectingIn?: number) => {
        if (!reconnectingIn) {
          dispatch('disconnect-fatal');
        } else {
          dispatch("disconnect");
          setHasBeenConnected(true);
          setReconnectingIn(reconnectingIn);
        }
      },
      onFDC3Ready: (api) => {
        api.getUserChannels().then((chans) => setChannels(chans));
      },
      onChannelJoined: (chanId) => {
        setCurrentChannelId(chanId);
      },
      onChannelLeft: () => setCurrentChannelId(undefined),
    };
    if (agentImport) {
      const { createAgent } = agentImport.module;
      const api = await createAgent(agentImport.interopUrl, appId, agentConfig);
      return api;
    }
    return undefined;
  }, [agentImport, appId]);

  useEffect(() => {
    createTheAgent()
      .then((fdc3: DesktopAgent | undefined) => {
        if (!fdc3) {
          return;
        }
        console.log("setting FDC3 for the children");
        setFDC3(fdc3);
      })
      .catch((e: Error) => {
        console.error(`error creating agent - ${e.message}`);
        let errorMessage: string = e.message;
        if (errorMessage.startsWith(iframeEmbedErrorPrefix)) {
          errorMessage = errorMessage.substring(iframeEmbedErrorPrefix.length);
        }
        dispatch("error");
        setErrorMessage(errorMessage);
      });
  }, [createTheAgent]);

  return {
    fdc3,
    resolver,
    authenticator,
    anchorElement,
    setAnchorElement,
    localHandlers,
    setLocalHandlers,
    directoryProps,
    state,
    channels,
    currentChannelId,
    sessionOwner,
    storageAccess,
    errorMessage,
    reconnectingIn,
    hasBeenConnected,
  };
}
